
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

interface ServiceDetailsProps {
  activeTab: string;
}

const ServiceDetails = ({ activeTab }: ServiceDetailsProps) => {
  const services: Record<string, any> = {
    ocean: {
      title: 'Ocean Freight Services',
      description: 'Comprehensive sea shipping solutions for cargo of all sizes across global routes. Our ocean freight services provide cost-effective transportation for bulk shipments with reliable scheduling and complete cargo tracking.',
      image: 'https://public.readdy.ai/ai/img_res/46ba6057be537e8262dfc798611927fd.jpg',
      features: [
        'Full Container Load (FCL) and Less than Container Load (LCL)',
        'Door-to-door delivery services',
        'Customs clearance assistance',
        'Real-time vessel tracking',
        'Competitive freight rates',
        'Insurance coverage options',
      ],
      pricing: 'Starting from $500 per container',
    },
    air: {
      title: 'Air Freight Services',
      description: 'Fast and reliable air cargo services for time-sensitive shipments worldwide. Our air freight solutions ensure your goods reach their destination quickly with priority handling and express delivery options.',
      image: 'https://readdy.ai/api/search-image?query=modern%20white%20cargo%20airplane%20flying%20through%20clear%20blue%20sky%20with%20soft%20clouds%20professional%20aviation%20photography%20showing%20sleek%20aircraft%20design%20representing%20fast%20efficient%20air%20freight%20logistics%20and%20express%20shipping%20services&width=1200&height=800&seq=service-air-detail-001&orientation=landscape',
      features: [
        'Express and standard air freight options',
        'Temperature-controlled cargo handling',
        'Dangerous goods transportation',
        'Charter flight arrangements',
        'Airport-to-airport and door-to-door services',
        'Priority customs clearance',
      ],
      pricing: 'Starting from $5 per kg',
    },
    road: {
      title: 'Road Transport Services',
      description: 'Efficient ground transportation with real-time tracking and flexible delivery options. Our extensive road network ensures timely deliveries across Europe with full truckload and partial load services.',
      image: 'https://readdy.ai/api/search-image?query=modern%20white%20delivery%20truck%20driving%20on%20smooth%20highway%20with%20green%20landscape%20background%20professional%20logistics%20photography%20showing%20clean%20commercial%20vehicle%20representing%20reliable%20road%20freight%20transportation%20and%20ground%20shipping%20services&width=1200&height=800&seq=service-road-detail-001&orientation=landscape',
      features: [
        'Full Truckload (FTL) and Less than Truckload (LTL)',
        'Refrigerated transport for perishables',
        'Oversized cargo handling',
        'Last-mile delivery solutions',
        'Cross-border transportation',
        'Scheduled and on-demand services',
      ],
      pricing: 'Starting from $200 per shipment',
    },
    rail: {
      title: 'Rail Logistics Services',
      description: 'Cost-effective rail freight solutions for bulk cargo and long-distance transport. Our rail logistics services offer an environmentally friendly alternative for heavy and oversized shipments across continental routes.',
      image: 'https://readdy.ai/api/search-image?query=long%20freight%20train%20with%20cargo%20containers%20traveling%20through%20scenic%20countryside%20landscape%20professional%20railway%20logistics%20photography%20showing%20efficient%20rail%20transportation%20with%20natural%20background%20representing%20sustainable%20bulk%20cargo%20shipping%20solutions&width=1200&height=800&seq=service-rail-detail-001&orientation=landscape',
      features: [
        'Intermodal container transport',
        'Bulk cargo handling',
        'Specialized railcar options',
        'Europe-Asia rail corridors',
        'Eco-friendly transportation',
        'Competitive transit times',
      ],
      pricing: 'Starting from $400 per container',
    },
    warehousing: {
      title: 'Warehousing & Distribution',
      description: 'State-of-the-art warehousing facilities with advanced inventory management systems. Our strategically located warehouses provide secure storage, order fulfillment, and distribution services to optimize your supply chain.',
      image: 'https://readdy.ai/api/search-image?query=modern%20warehouse%20interior%20with%20organized%20shelving%20systems%20and%20cargo%20storage%20professional%20logistics%20facility%20photography%20showing%20clean%20organized%20space%20with%20good%20lighting%20representing%20efficient%20inventory%20management%20and%20distribution%20center%20operations&width=1200&height=800&seq=service-warehouse-001&orientation=landscape',
      features: [
        'Climate-controlled storage facilities',
        'Inventory management systems',
        'Order picking and packing',
        'Cross-docking services',
        'Value-added services (labeling, kitting)',
        '24/7 security and monitoring',
      ],
      pricing: 'Starting from $50 per pallet/month',
    },
    customs: {
      title: 'Customs Clearance Services',
      description: 'Expert customs brokerage and compliance services to ensure smooth international shipments. Our experienced team handles all documentation, duties, and regulations to expedite your cargo through customs.',
      image: 'https://readdy.ai/api/search-image?query=professional%20customs%20office%20with%20documents%20and%20cargo%20inspection%20area%20clean%20organized%20workspace%20representing%20efficient%20customs%20clearance%20and%20international%20trade%20compliance%20services%20with%20modern%20facilities&width=1200&height=800&seq=service-customs-001&orientation=landscape',
      features: [
        'Import and export documentation',
        'Duty and tax calculation',
        'Tariff classification',
        'Compliance consulting',
        'Bonded warehouse services',
        'Fast-track clearance options',
      ],
      pricing: 'Starting from $100 per shipment',
    },
  };

  const service = services[activeTab];

  return (
    <motion.section
      key={activeTab}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="py-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <h2 className="font-heading text-4xl lg:text-5xl text-gray-900 font-bold mb-6">{service.title}</h2>
            <p className="text-lg text-gray-600 mb-8">{service.description}</p>
            <div className="bg-primary-blue text-white rounded-xl p-6 mb-8">
              <div className="text-sm font-medium mb-2">Pricing</div>
              <div className="text-2xl font-bold">{service.pricing}</div>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-primary-green text-white font-semibold rounded-lg hover:bg-primary-green/90 transition-all whitespace-nowrap cursor-pointer"
            >
              Request Quote
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>

          <div className="relative">
            <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-xl">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-8 lg:p-12">
          <h3 className="text-2xl font-heading font-bold text-gray-900 mb-8">Key Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {service.features.map((feature: string, index: number) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-green rounded-lg flex-shrink-0 mt-1">
                  <i className="ri-check-line text-white"></i>
                </div>
                <p className="text-gray-700 text-lg">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default ServiceDetails;
